import { useEffect, useRef, useState } from "react";
import { useSubscription } from "@apollo/client/react";
import { BalanceImpact, Currency, CurrencyAmount, FungibleToken, FungibleTokenAmount, rawAmountToDecimalString, notEmpty, toDate, } from "@orb-labs/types";
import { InstrumentTradeUpdatesSubscription } from "../../graphql/subscriptions.js";
const MAX_STREAMED_TRADES = 200;
const FIAT_DECIMALS = 6;
const fiatCurrency = new Currency(FIAT_DECIMALS, "USD", "US Dollar");
function parseBalanceImpact(value) {
    if (value === "positive" || value === "POSITIVE")
        return BalanceImpact.POSITIVE;
    if (value === "negative" || value === "NEGATIVE")
        return BalanceImpact.NEGATIVE;
    return BalanceImpact.NONE;
}
/**
 * Parses a human-readable decimal string (e.g. "12.85") into a BigInt raw
 * amount and the number of decimal places, using pure string manipulation
 * to avoid floating-point precision loss.
 */
function parseDecimalToRaw(value) {
    if (!value)
        return { raw: BigInt(0), decimals: 0 };
    const negative = value.startsWith("-");
    const abs = negative ? value.slice(1) : value;
    const dotIdx = abs.indexOf(".");
    if (dotIdx === -1) {
        const raw = BigInt(abs || "0");
        return { raw: negative ? -raw : raw, decimals: 0 };
    }
    const whole = abs.slice(0, dotIdx) || "0";
    const frac = abs.slice(dotIdx + 1);
    const raw = BigInt(whole + frac);
    return { raw: negative ? -raw : raw, decimals: frac.length };
}
function scaleToDecimals(raw, fromDecimals, toDecimals) {
    const diff = toDecimals - fromDecimals;
    if (diff === 0)
        return raw;
    if (diff > 0)
        return raw * BigInt(10 ** diff);
    return raw / BigInt(10 ** -diff);
}
function subscriptionTradeToTrade(raw, tokenDecimalsFallback) {
    let { raw: amountRaw, decimals: amountDecimals } = parseDecimalToRaw(raw.amount);
    if (amountDecimals === 0 &&
        !raw.amount.includes(".") &&
        notEmpty(tokenDecimalsFallback) &&
        tokenDecimalsFallback > 0) {
        try {
            const human = rawAmountToDecimalString(BigInt(raw.amount.trim()), tokenDecimalsFallback);
            const reparsed = parseDecimalToRaw(human);
            amountRaw = reparsed.raw;
            amountDecimals = reparsed.decimals;
        }
        catch {
            // keep amountRaw / amountDecimals from integer-only parse
        }
    }
    const token = new FungibleToken(BigInt(1), "", amountDecimals, raw.symbol, raw.symbol, undefined, undefined, true);
    const { raw: fiatRaw, decimals: fiatParsedDecimals } = parseDecimalToRaw(raw.fiatValue);
    const scaledFiatRaw = scaleToDecimals(fiatRaw, fiatParsedDecimals, FIAT_DECIMALS);
    const fiatAmount = CurrencyAmount.fromRawAmount(fiatCurrency, scaledFiatRaw);
    return {
        accountAddress: raw.accountAddress,
        amount: FungibleTokenAmount.fromRawAmount(token, amountRaw, fiatAmount),
        balanceImpact: parseBalanceImpact(raw.balanceImpact),
        fiatPnl: CurrencyAmount.fromRawAmount(fiatCurrency, BigInt(0)),
        timestamp: toDate(raw.timestamp) ?? new Date(raw.timestamp),
    };
}
/**
 * Subscribes to real-time individual trade events for a specific instrument.
 * Only active when standardizedInstrumentId is provided (e.g. user is on the detail page).
 * Returns an array of Trade objects, newest first.
 */
export function useInstrumentTradeSubscription(standardizedInstrumentId, 
/** When the server sends a raw integer amount (legacy), scale using anchor token decimals. */
tokenDecimalsFallback) {
    const [trades, setTrades] = useState([]);
    const prevId = useRef(standardizedInstrumentId);
    const decimalsRef = useRef(tokenDecimalsFallback);
    decimalsRef.current = tokenDecimalsFallback;
    useEffect(() => {
        if (prevId.current !== standardizedInstrumentId) {
            setTrades([]);
            prevId.current = standardizedInstrumentId;
        }
    }, [standardizedInstrumentId]);
    useSubscription(InstrumentTradeUpdatesSubscription, {
        variables: { input: { standardizedInstrumentId } },
        skip: !standardizedInstrumentId,
        onData: ({ data }) => {
            const raw = data?.data?.instrumentTradeUpdates;
            if (!raw)
                return;
            const trade = subscriptionTradeToTrade(raw, decimalsRef.current);
            setTrades((prev) => [trade, ...prev].slice(0, MAX_STREAMED_TRADES));
        },
    });
    return trades;
}
